import { useEffect, useState, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useTenants } from "../../hooks/useTenants";
import { myLocalStorage } from "../StorageHelper";

export default function TenantLists({
  onSelectorChange,
  isLabelNeeded = true,
}: {
  onSelectorChange?: (value: string) => void;
  isLabelNeeded?: boolean;
}) {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const { tenants, loading } = useTenants();
  const cachedTenantName =
    myLocalStorage.getItem("latestTenant")?.tenantName
    || myLocalStorage.getItem("latestTenant")
    || "";

  useEffect(() => {
    if (cachedTenantName && !selectedOption) {
      setSelectedOption(cachedTenantName);
      onSelectorChange?.(cachedTenantName);
    }
  }, [cachedTenantName, onSelectorChange, selectedOption]);

  useEffect(() => {
    if (tenants.length > 0) {
      const latestTenant = myLocalStorage.getItem("latestTenant");

      const existingTenant = tenants.find(
        (tenant) =>
          tenant.tenantName === latestTenant?.tenantName ||
          tenant.tenantName === latestTenant
      );

      if (existingTenant) {
        myLocalStorage.setItem("latestTenant", existingTenant);
        setSelectedOption(existingTenant.tenantName);
        onSelectorChange?.(existingTenant.tenantName);
      } else {
        const firstTenant = tenants[0];
        setSelectedOption(firstTenant.tenantName);
        onSelectorChange?.(firstTenant.tenantName);
        myLocalStorage.setItem("latestTenant", firstTenant);
      }
    }
  }, [tenants]);

  const displayTenantName = selectedOption || cachedTenantName;

  const handleSelectorChange = (tenant: any) => {
    setSelectedOption(tenant.tenantName);
    onSelectorChange?.(tenant.tenantName);
    myLocalStorage.setItem("latestTenant", tenant);
  };

  const filteredTenants = useMemo(() => {
    const term = search.toLowerCase();
    return tenants.filter((tenant) =>
      tenant.tenantName.toLowerCase().includes(term)
    );
  }, [tenants, search]);

  if (loading && tenants.length === 0 && !cachedTenantName) {
    return <div>Loading...</div>;
  }
  if (!loading && tenants.length === 0 && !cachedTenantName) {
    return <div>No tenants found</div>;
  }

  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="relative">
        {isLabelNeeded && (
          <label
            htmlFor="tenant-selector"
            className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
          >
            Tenant List
          </label>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              id="tenant-selector"
              variant="outline"
              className="min-w-[220px] justify-between rounded-md"
              data-testid="dropdown-selector"
            >
              {displayTenantName || "Select tenant"}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="min-w-[220px] max-h-[300px] overflow-y-auto">
            {/* 🔍 Search bar */}
            <div className="flex items-center px-2 py-1.5 sticky top-0 bg-background border-b">
              <Search className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenant..."
                className="w-full text-sm bg-transparent outline-none"
              />
            </div>

            {filteredTenants.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No tenants found
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.domainName}
                  onClick={() => handleSelectorChange(tenant)}
                >
                  {tenant.tenantName}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
